/**
 * [rev17] fieldSpec — canonical transaction field registry
 *
 * Single source of truth for the field metadata that both Detail (read) and
 * Edit/Add (form) modals render via `<TransactionFieldRow>`. Cross-modal parity
 * (rev17 plan D1 "Unified Ledger Sheet", D2 "one responsive row contract", D3
 * "preserve canonical order, with explicit contextual fields") is enforced by
 * both modes iterating the same registry instead of hand-written `<DetailRow>`
 * / `<FormField>` blocks. The order constants feed:
 *
 *   - Detail's `data-testid="txn-field-row-{key}"` sequence
 *   - Edit/Add's `data-testid="txn-field-input-{key}"` / `txn-field-trigger-{key}` sequence
 *   - Rev16 unification tests (kept green; rev17 preserves the canonical order)
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * CANONICAL ORDER (rev17 plan D3, locked)
 * ─────────────────────────────────────────────────────────────────────────────
 *
 *   type -> amount -> date -> accountId -> categoryId -> tags -> items
 *        -> notes -> receipt -> emoji
 *
 *   `FIELD_ORDER` is derived deterministically from the exported registries,
 *   so adding a new field is a one-line edit here plus its consumer mapping.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * FIELD BOUNDARY (rev17 plan D3 — explicit, not duplicated)
 * ─────────────────────────────────────────────────────────────────────────────
 *
 *   1. `description` (transaction name) is the **identity field**. It is owned
 *      by the Detail/Edit *hero* header only. It must NOT be duplicated as a
 *      second visible row in PRIMARY_FIELDS or SECONDARY_FIELDS. Editing the
 *      hero shortcut opens Edit at the description focus target — see
 *      `useModalStack` `focusField` metadata (plan D4) and
 *      `TransactionDetailSheet` hero wiring.
 *
 *   2. `toAccountId` is a **contextual** secondary field that follows
 *      `accountId` ONLY when `type === 'transfer'`. It is intentionally
 *      omitted from the PRIMARY_FIELDS / SECONDARY_FIELDS arrays: it has no
 *      fixed slot in the canonical order and is rendered by the existing
 *      transfer-aware block in `TransactionForm` / `TransactionDetailSheet`.
 *      Adding it here as a generic row would break the conditional render
 *      contract and AC4 (Detail and Edit canonical fields appear in the same
 *      order; transfer/items remain conditional).
 *
 *   3. `currency` and `discountAmount` are SECONDARY (collapsible "More �"
 *      disclosure in Edit; secondary section in Detail). They never appear in
 *      the primary row sequence.
 *
 *   4. `recurringPattern` from the plan pseudo-code is intentionally OMITTED.
 *      Only `isRecurring: boolean` exists on the schema. Adding
 *      `recurringPattern` here would require inventing fields the form does
 *      not own (forbidden by the rev17 contract — see also the existing
 *      schema in `TransactionForm.tsx`).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * CUSTOM RENDERER BOUNDARIES (rev17 — no invented form paths)
 * ─────────────────────────────────────────────────────────────────────────────
 *
 *   `items` and `receipt` are **custom renderer boundaries**: their canonical
 *   position is preserved (rows 70 and 90 respectively) but the entries carry
 *   metadata only — they are NOT direct `TransactionFormData` paths in the
 *   way `amount` / `accountId` / `tags` are. The actual form fields are:
 *
 *     - `items`  -> `items: TransactionItem[]` (field array; rendered by
 *                   `ItemsEditorSection` inside `TransactionForm` and
 *                   `ItemsDetailSection` inside `TransactionDetailSheet`).
 *                   The row entry exists so the visible canonical order is
 *                   stable from this registry.
 *
 *     - `receipt` -> `receiptAttachment` (Detail read) and the pending
 *                   receipt quadruple (`pendingReceiptId`,
 *                   `pendingReceiptFilename`, `pendingReceiptMimeType`,
 *                   `pendingReceiptSize`) in Edit/Add (there is no single
 *                   `receipt` form path). Wiring the read-mode renderer and
 *                   the edit-mode file picker to those pending fields is the
 *                   rev17 Detail/Form responsibility, NOT this registry.
 *                   The entry stays in PRIMARY_FIELDS so the visible order
 *                   is locked.
 *
 *   Consumers (`TransactionFieldRow`, `TransactionDetailSheet`,
 *   `TransactionForm`) MUST treat these two keys as renderer boundaries and
 *   resolve them via the existing per-section renderers rather than the
 *   generic `<TransactionFieldRow>` path. No new metadata field is added to
 *   `FieldSpec` (which lives in `TransactionFieldRow.tsx`) — the boundary
 *   contract here is comment-typed and stable across consumers.
 *
 * @see docs/superpowers/plans/2026-08-16-rev17-modal-redesign-plan.md (D3, D4)
 * @see docs/superpowers/plans/2026-08-16-rev16-modal-redesign-plan.md
 *      (predecessor; rev17 preserves the canonical order it locked)
 */
import {
  CalendarDays,
  DollarSign,
  Hash,
  Layers,
  Paperclip,
  Smile,
  StickyNote,
  Tag,
  Wallet,
} from 'lucide-react';

import type { FieldSpec } from './TransactionFieldRow';

/**
 * Canonical PRIMARY_FIELDS registry — array order matches the rev17 plan D3
 * sequence exactly.
 *
 * Each entry's `key` is the canonical field key consumed by
 * `<TransactionFieldRow>` in both read and edit modes. The array ORDER
 * (not just `order` numbers) is what consumers iterate, so reordering
 * entries here reshuffles Detail and Edit identically.
 *
 * Boundary notes:
 *   - `description` is hero-only and intentionally absent from this list
 *     (see file header).
 *   - `toAccountId` is contextual/transfers-only and intentionally absent
 *     from this list (see file header).
 *   - `items` and `receipt` are custom renderer boundaries; their rows are
 *     metadata-only and resolved by per-section renderers in the Detail and
 *     Form consumers (see file header).
 */
export const PRIMARY_FIELDS: FieldSpec[] = [
  {
    key: 'type',
    label: 'Type',
    icon: CalendarDays,
    required: true,
    order: 10,
    options: [
      { value: 'income', label: 'Income' },
      { value: 'expense', label: 'Expense' },
      { value: 'transfer', label: 'Transfer' },
    ],
  },
  {
    key: 'amount',
    label: 'Amount',
    icon: DollarSign,
    required: true,
    order: 20,
    tooltip: 'Enter the absolute amount. The type field handles sign.',
  },
  {
    key: 'date',
    label: 'Date',
    icon: CalendarDays,
    required: true,
    order: 30,
    tooltip: 'The date the transaction actually occurred.',
  },
  {
    key: 'accountId',
    label: 'Account',
    icon: Wallet,
    required: true,
    order: 40,
    tooltip: 'Which account did the money leave or enter?',
  },
  {
    key: 'categoryId',
    label: 'Category',
    icon: Tag,
    optional: true,
    order: 50,
    tooltip: 'Assigns this transaction to a budget category.',
  },
  {
    key: 'tags',
    label: 'Tags',
    icon: Hash,
    optional: true,
    order: 60,
    tooltip: 'Press Enter or comma to add a tag.',
  },
  {
    key: 'items',
    label: 'Line Items',
    icon: Layers,
    optional: true,
    order: 70,
    // Custom renderer boundary (rev17). The actual form path is
    // `items: TransactionItem[]`; this entry carries metadata only so the
    // canonical order slot is locked. Rendering is handled by
    // `ItemsEditorSection` in TransactionForm and `ItemsDetailSection` in
    // TransactionDetailSheet. Consumers must NOT route this key through the
    // generic `<TransactionFieldRow>` path.
  },
  {
    key: 'notes',
    label: 'Notes',
    icon: StickyNote,
    optional: true,
    order: 80,
  },
  {
    key: 'receipt',
    label: 'Receipt',
    icon: Paperclip,
    optional: true,
    order: 90,
    // Custom renderer boundary (rev17). There is no single `receipt`
    // `TransactionFormData` path: Detail reads `receiptAttachment` and
    // Edit/Add writes the pending-receipt quadruple
    // (`pendingReceiptId`, `pendingReceiptFilename`, `pendingReceiptMimeType`,
    // `pendingReceiptSize`). This entry carries metadata only so the
    // canonical order slot is locked. Consumers must NOT route this key
    // through the generic `<TransactionFieldRow>` path; use the existing
    // `ReceiptDetailSection` / receipt-picker renderers instead.
  },
  {
    key: 'emoji',
    label: 'Icon',
    icon: Smile,
    optional: true,
    order: 100,
    tooltip: 'A visual tag for quick scanning in transaction lists.',
  },
];

/**
 * Secondary fields — rendered inside the collapsible "More ▾" disclosure in
 * Edit/Add and inside the read-mode secondary section in Detail.
 *
 * Per rev17 plan D3: only `currency` and `discountAmount` qualify as
 * secondary. `recurringPattern` is intentionally omitted because only
 * `isRecurring: boolean` exists on the form schema (see file header). The
 * contextual transfer `toAccountId` is also intentionally absent from this
 * list — it is rendered conditionally by the transfer-aware block, not via
 * the generic secondary disclosure.
 */
export const SECONDARY_FIELDS: FieldSpec[] = [
  {
    key: 'currency',
    label: 'Currency',
    icon: DollarSign,
    optional: true,
    order: 200,
    tooltip: 'Override the account default currency.',
  },
  {
    key: 'discountAmount',
    label: 'Discount',
    icon: DollarSign,
    optional: true,
    order: 210,
  },
];

/**
 * Deterministic field-order list derived from the exported registries.
 * Sorting by `order` ensures both Detail and Edit/Add iterate the keys in
 * the same canonical sequence regardless of which registry an entry was
 * added to. Public API; preserved across rev16 → rev17.
 */
export const FIELD_ORDER: readonly string[] = [
  ...PRIMARY_FIELDS,
  ...SECONDARY_FIELDS,
]
  .sort((a, b) => a.order - b.order)
  .map((spec) => spec.key);
